import { useNavigate, useParams } from "react-router-dom";
import { useGetResultsetPredictionsMetadataResultsetPredictionsResultsetIdGet, useGetResultsetV2ResultsetDataframeV2ResultsetIdGet, useGetSinglePredictionResultsetsResultsetSinglePredictionGet } from "../../../api/generated/fastAPI";
import { useEffect, useState, MouseEvent, useMemo, SyntheticEvent } from "react";
import { Model, ModelFamily, Prediction, Resultset } from "../../../api/models";
import SinglePageHeader from "../../../components/Page/SinglePageHeader";
import NestedDropdown from "../../../components/Menus&Dropdowns/NestedDropdown";
import { DropdownOption } from "../../../components/Menus&Dropdowns/Dropdown";
import SnackBar from "../../../components/Alerts/SnackBar";
import usePaginate from "../../../hooks/usePaginate";
import useMessage from "../../../hooks/useMessage";
import { TableData, TableDataField, TableHeader } from "../../../components/Table/DataTable";
import { customInstance } from "../../../api/custom-instance";
import { exportFileExtensions } from "../../../utils/supplementalData";
import NewPageTabs from "../../../components/Tabs/NewPageTabs";
import MoleculeStructure from "../../../components/Data/MoleculeStructure";
import Pagination from "../../../components/Filtering/Pagination";
import HorizontalNumberSlider from "../../../components/Data/HorizontalNumberSlider";
import ColumnsButton from "../../../components/Buttons/ColumnsButton";
import SelectField from "../../../components/Form/SelectFields";
import DeleteModal from "../../../components/Popups/DeleteModal";
import { exportData } from "../../../utils/formattingFns";
import useDelete from "../../../hooks/endpoints/useDelete";
import { formatDisplayedResultsetMetadata, formatResultsetDataframe, getModelTaskType, MetadataMenuItemType } from "../../../utils/dataFormattingFns";
import UpdatedMetadataMenu from "../../../components/Menus&Dropdowns/UpdatedMetadataMenu";
import VirtualizedDataTable from "../../../components/Table/VirtualizedDataTable";
import { Skeleton } from "@mui/material";
import ResultsetVisualizations from "../components/ResultsetVisualizations";
import ResultsetExplainabilityTab from "../components/ResultsetExplainabilityTab";
import BooleanField from "../../../components/Form/BooleanField";
import { ResultsetGrouping } from "./IndividualResultset";
import DCMTrustTab from "../components/DCMTrustTab";
import ResultComparison from "../components/ResultComparison";

const NON_COMPARISON_COLUMNS = new Set(['structure/SMILES', 'smiles']);

const toThresholdOrNull = (value: unknown): number | null => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
        return null;
    }
    return Math.max(0, Math.min(1, numericValue));
};

const extractExplicitDcmThreshold = (prediction: Prediction): number | null => {
    const modelMetadata = (prediction as any)?.model_metadata ?? {};
    const dcmMetadata = modelMetadata?.dcm_metadata ?? {};
    const dcmParams = dcmMetadata?.job_metadata?.dcm_params ?? {};
    const runtimeMetrics = dcmMetadata?.runtime_metrics ?? {};

    const candidates = [
        dcmParams.domain_threshold,
        dcmParams.inference_domain_threshold,
        dcmParams.threshold,
        runtimeMetrics.domain_threshold,
        runtimeMetrics.inference_domain_threshold,
    ];

    for (const candidate of candidates) {
        const threshold = toThresholdOrNull(candidate);
        if (threshold !== null) {
            return threshold;
        }
    }

    return null;
};

const getDefaultDcmThreshold = (
    grouping: ResultsetGrouping | null,
    predictionItems: Prediction[],
    selectedDcmModelHash?: string | null,
): number => {
    if (!grouping || !grouping.hasDcm || predictionItems.length === 0) return 0.5;

    const dcmModelHashes = grouping.dcmModelHashes ?? [];
    if (dcmModelHashes.length === 0) return 0.5;

    const prioritizedHashes = selectedDcmModelHash
        ? [selectedDcmModelHash, ...dcmModelHashes.filter((hash) => hash !== selectedDcmModelHash)]
        : dcmModelHashes;

    for (const hash of prioritizedHashes) {
        const matchingPrediction = predictionItems.find((prediction) => String(prediction.model_hash ?? '') === hash);
        if (!matchingPrediction) {
            continue;
        }

        const threshold = extractExplicitDcmThreshold(matchingPrediction);
        if (threshold !== null) {
            return threshold;
        }
    }

    return 0.5;
};

const buildSingleCompoundComparisonData = (
    groupedResultsetData: ResultsetGrouping[],
    selectedModelFamilyIds: number[],
    smilesValue?: string | null,
): {
    headers: TableHeader[];
    data: TableData[];
    hasMoleculeHighlighting: boolean;
} => {
    const modelFamilyHeaders: TableHeader[] = [
        {
            label: 'Model Family Name',
            value: 'name',
            isShown: true,
            alignment: 'left',
        },
    ];

    const metricHeaders: TableHeader[] = [];
    const methodHeaders: TableHeader[] = [];
    let hasMoleculeHighlighting = false;

    const rows: TableData[] = groupedResultsetData
        .filter((group: ResultsetGrouping) => selectedModelFamilyIds.includes(group.modelFamilyId))
        .map((group: ResultsetGrouping) => {
            const firstDataRow = group.data?.[0];
            const fieldLookup = new Map<string, TableDataField>();

            if (firstDataRow?.fields?.length > 0) {
                firstDataRow.fields.forEach((field: TableDataField) => {
                    fieldLookup.set(field.associatedColumn, field);
                });
            }

            const rowFields: TableDataField[] = [
                {
                    associatedColumn: 'name',
                    value: group.modelFamilyName,
                    type: 'text',
                    alignment: 'left',
                },
            ];

            (group.metricsHeaders ?? []).forEach((header: TableHeader) => {
                if (NON_COMPARISON_COLUMNS.has(header.value)) {
                    return;
                }

                // Use label as canonical column key so that AD columns with
                // different IDs but the same metric type (e.g. RDNMACCSTanimotoSimilarity/42
                // vs RDNMACCSTanimotoSimilarity/67) merge into a single column.
                const metricColumnKey = header.label;

                if (!metricHeaders.find((existing) => existing.value === metricColumnKey)) {
                    metricHeaders.push({
                        ...header,
                        value: metricColumnKey,
                        isShown: true,
                        alignment: 'center',
                    });
                }

                const sourceField = fieldLookup.get(header.value);
                rowFields.push(
                    sourceField
                        ? {
                            ...sourceField,
                            associatedColumn: metricColumnKey,
                            alignment: 'center',
                        }
                        : {
                            associatedColumn: metricColumnKey,
                            value: 'None',
                            type: 'text',
                            alignment: 'center',
                        } as TableDataField,
                );
            });

            (group.modelHeaders ?? []).forEach((header: TableHeader) => {
                const methodColumnKey = header.label;

                if (!methodHeaders.find((existing) => existing.value === methodColumnKey)) {
                    methodHeaders.push({
                        label: header.label,
                        value: methodColumnKey,
                        isShown: true,
                        alignment: 'center',
                    });
                }

                const sourceField = fieldLookup.get(header.value);
                rowFields.push(
                    sourceField
                        ? {
                            ...sourceField,
                            associatedColumn: methodColumnKey,
                            alignment: 'center',
                        }
                        : {
                            associatedColumn: methodColumnKey,
                            value: 'None',
                            type: 'text',
                            alignment: 'center',
                        } as TableDataField,
                );
            });

            if ((group.moleculeHighlightingHeaders ?? []).length > 0) {
                hasMoleculeHighlighting = true;
            }

            return {
                id: String(group.modelFamilyId),
                fields: rowFields,
            };
        });

    methodHeaders.sort((a, b) => {
        if (a.label < b.label) return -1;
        if (a.label > b.label) return 1;
        return 0;
    });

    let headers = modelFamilyHeaders.concat(metricHeaders).concat(methodHeaders);
    let rowData = rows;

    if (hasMoleculeHighlighting) {
        const moleculeHeader: TableHeader = {
            label: 'Molecule Highlighting',
            value: 'molecule_highlighting',
            isShown: true,
            alignment: 'center',
        };

        headers = [
            moleculeHeader,
            {
                ...modelFamilyHeaders[0],
            },
        ].concat(headers.filter((header) => header.value !== 'name'));

        rowData = rows.map((row) => ({
            ...row,
            fields: [
                {
                    associatedColumn: 'molecule_highlighting',
                    value: smilesValue ?? '',
                    type: 'text',
                    alignment: 'center',
                } as TableDataField,
                ...row.fields,
            ],
        }));
    }

    const normalizedRows: TableData[] = rowData.map((row) => ({
        ...row,
        fields: headers.map((header): TableDataField => {
            const sourceField = row.fields.find((field) => field.associatedColumn === header.value);

            if (!sourceField) {
                return {
                    associatedColumn: header.value,
                    value: 'None',
                    type: 'text',
                    alignment: 'center',
                } as TableDataField;
            }

            const isNil = sourceField.value === null || sourceField.value === undefined;
            return {
                ...sourceField,
                value: isNil ? 'None' : sourceField.value,
                type: isNil ? 'text' : sourceField.type,
            };
        }),
    }));

    return {
        headers,
        data: normalizedRows,
        hasMoleculeHighlighting,
    };
};

const getModelFamiliesAndModels = async (
    model_family_ids: { [key: string]: string[] },
    modelMethodNames?: { [hash: string]: { name: string, is_dcm: boolean } },
) => {
    const modelFamilyModelData: { 
        modelFamily: ModelFamily,
        models: Model[] 
    }[] = [];

    if (Object.entries(model_family_ids).length > 0) {
        for (const [model_family_id, model_family_model_ids] of Object.entries(model_family_ids)) {
            let modelFamily: ModelFamily | undefined;
            const models: Model[] = [];

            if (modelMethodNames && model_family_model_ids.length > 0) {
                // Fast path: build minimal Model objects from cached method names
                for (const hash of model_family_model_ids) {
                    const cached = modelMethodNames[hash];
                    if (cached) {
                        models.push({
                            file_hash: hash,
                            is_dcm: cached.is_dcm,
                            ml_method: cached.name,
                            model_metadata: {},
                        } as unknown as Model);
                    } else {
                        // Fallback: fetch individually for hashes not in cache
                        const response = await customInstance({
                            url: `model/?model_hash=${hash}`,
                            method: 'GET',
                        });
                        models.push(response as unknown as Model);
                    }
                }
            } else {
                // Legacy path: fetch all models individually
                const promises = model_family_model_ids.map(async (id) => {
                    const response = await customInstance({
                        url: `model/?model_hash=${id}`,
                        method: 'GET',
                    });
                    const model = response as unknown as Model;
                    return models.push(model);
                });
                await Promise.all(promises);
            }

            try {
                const modelFamilyResponse = await customInstance({
                    url: `model_family/?model_family_id=${model_family_id}&include_model_family_metadata=true&include_model_stats=false&include_airflow_metadata=false`,
                    method: 'GET',
                });

                modelFamily = modelFamilyResponse as unknown as ModelFamily;
            } catch (err:any) {
                console.log(err);
            }

            if (modelFamily && models.length > 0) {
                modelFamilyModelData.push({
                    modelFamily,
                    models,
                });
            }
        }
    }

    return modelFamilyModelData;
}

export interface CategorizedModels {
    classification: {
        modelFamily: ModelFamily,
        models: Model[],
    }[],
    regression: {
        modelFamily: ModelFamily,
        models: Model[],
    }[],
}

export default function IndividualCompoundPredictionV2() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [ prediction, setPrediction ] = useState<Resultset | null>(null);
    const [ predictions, setPredictions ] = useState<Prediction[]>([]);
    const [ predictionData, setPredictionData ] = useState<TableData[]>([]);
    const [ groupedResultsetData, setGroupedResultsetData ] = useState<ResultsetGrouping[]>([]);
    const [ predictionMetadata, setPredictionMetadata ] = useState<MetadataMenuItemType[]>([]);
    const [ dataframe, setDataframe ] = useState<any | null>(null);

    const getPredictionMutation = useGetSinglePredictionResultsetsResultsetSinglePredictionGet({
        resultset_id: id ? id : ''
    });

    const getResultsetPredictionMetadata = useGetResultsetPredictionsMetadataResultsetPredictionsResultsetIdGet(id ? Number(id) : 0);

    const [ modelFamilyModels, setModelFamilyModels ] = useState<CategorizedModels>({
        classification: [],
        regression: [],
    });
    const [ dcmModelFamilyIds, setDcmModelFamilyIds ] = useState<number[]>([]);


    const getResultsetDataframeV2 = useGetResultsetV2ResultsetDataframeV2ResultsetIdGet(id ? Number(id) : 0, {
        applicability_domain: true,
        include_duplicate_values: true,
        include_duplicates: true,
        include_molecule_highlighting: false,
    });
    

    const [ headers, setHeaders ] = useState<TableHeader[]>([]);
    const [ threshold, setThreshold ] = useState<number>(0.5);
    const [ dcmThreshold, setDcmThreshold ] = useState<number>(0.5);
    const [ selectedDcmModelHash, setSelectedDcmModelHash ] = useState<string | null>(null);
    const [ isConformal, setIsConformal ] = useState(false);
    const [ selectedIndex, setSelectedIndex ] = useState(0);
    const [ chosenExplainabilityModelFamily, setChosenExplainabilityModelFamily ] = useState<{ name: string, id: number } | null>(null);

    const [ isLoading, setIsLoading ] = useState(false);
    const [ isModelLoading, setIsModelLoading ] = useState(false);
    const [ isDataLoading, setIsDataLoading ] = useState(false);

    const [useProba, setUseProba] = useState(false);
    const [hasProba, setHasProba] = useState(false);

     const {
            setItems,
            setItemsPerPage,
            setCurrentPage,
            currentPage,
            paginatedItems,
            startIndex,
            endIndex,
            totalPages,
            paginate,
        } = usePaginate();
    
        const {
            open,
            setOpen,
            message,
            isSuccess,
            setPopupMessage
        } = useMessage();

    const { deleteResultsets } = useDelete({ setPopupMessage });


    useEffect(() => {
        if (getPredictionMutation.isLoading || getResultsetPredictionMetadata.isLoading || getResultsetDataframeV2.isLoading || isModelLoading || isDataLoading) {
            setIsLoading(true);
        } else {
            setIsLoading(false);
        }
    }, [
        getPredictionMutation.isLoading, 
        getResultsetPredictionMetadata.isLoading,
        getResultsetDataframeV2.isLoading,
        isModelLoading,
        isDataLoading,
    ]);

    useEffect(() => {
        if (getPredictionMutation.data) {
            const pred = getPredictionMutation.data as unknown as Resultset;
            setPrediction(pred);

        }
    }, [getPredictionMutation.data]);

    useEffect(() => {
        const fetchPredictionMetadata = async (pred: Resultset, modelFamilyFiles: { id: number, name: string }[]) => {
            const metadata = await formatDisplayedResultsetMetadata(pred, [], modelFamilyFiles);
            setPredictionMetadata(metadata);
        };

        if (prediction && (modelFamilyModels.classification.length > 0 || modelFamilyModels.regression.length > 0)) {
            const modelFamilyFiles: { id: number, name: string }[] = [];

            const { classification, regression } = modelFamilyModels;
            [classification, regression].forEach((modelType: { modelFamily: ModelFamily, models: Model[] }[]) => {
                modelType.forEach((modelGrouping: { modelFamily: ModelFamily, models: Model[] }) => {
                    const modelFamily = modelGrouping.modelFamily;
                    modelFamilyFiles.push({
                        id: modelFamily.id!,
                        name: modelFamily.name ?? '',
                    })
                })
            });

            fetchPredictionMetadata(prediction, modelFamilyFiles);
        }
    }, [prediction, modelFamilyModels]);

    useEffect(() => {
        if (getResultsetPredictionMetadata.data) {
            const metadata = getResultsetPredictionMetadata.data as unknown as Prediction[];
            setPredictions(metadata);
        }
    }, [getResultsetPredictionMetadata.data]);

    useEffect(() => {
        const formatSharedResultsetData = async () => {
            if (!dataframe || predictions.length === 0) {
                setGroupedResultsetData([]);
                return;
            }

            setIsDataLoading(true);
            try {
                const formattedData = await formatResultsetDataframe(dataframe, predictions, useProba);
                setGroupedResultsetData(formattedData.groupedRsData ?? []);
            } catch (err: any) {
                console.log(err);
                setGroupedResultsetData([]);
            }
            setIsDataLoading(false);
        };

        formatSharedResultsetData();
    }, [dataframe, predictions, useProba]);

    useEffect(() => {
        if (getResultsetDataframeV2.data) {
            const data = getResultsetDataframeV2.data as unknown as any;
            setDataframe(data);
        }
    }, [getResultsetDataframeV2.data]);

    useEffect(() => {
        const categorizeModelData = async (model_family_ids: { [key: string]: string[] }) => {
            setIsModelLoading(true);
            setDcmModelFamilyIds([]);

            try {
                const modelMethodNames = dataframe?.model_method_names ?? undefined;
                const modelFamilyModelData = await getModelFamiliesAndModels(model_family_ids, modelMethodNames);

                if (modelFamilyModelData.length > 0) {

                    let classification: {
                        modelFamily: ModelFamily,
                        models: Model[],
                    }[] = [];

                    let regression: {
                        modelFamily: ModelFamily,
                        models: Model[],
                    }[] = [];

                    const dcmIds: number[] = [];

                    modelFamilyModelData.forEach(family => {
                        const model = family.models[0];
                        const taskType = getModelTaskType(model);

                        if (taskType === 'regression') {
                            regression.push(family)
                        } else {
                            classification.push(family)
                            if (taskType === 'dcm' && family.modelFamily.id !== undefined && family.modelFamily.id !== null) {
                                dcmIds.push(family.modelFamily.id);
                            }
                        }
                    });

                    setModelFamilyModels({
                        classification,
                        regression,
                    });
                    setDcmModelFamilyIds(Array.from(new Set(dcmIds)));

                }
            } catch (err:any) {
                console.log(err);
            }

            setIsModelLoading(false);

            
        }

        if (prediction && prediction.resultset_metadata!.model_family_ids && dataframe) {
            categorizeModelData(prediction.resultset_metadata!.model_family_ids as { [key: string]: string[] });
        }
    }, [prediction, dataframe]);

    const pageTabs: { label: string, value: string }[] = useMemo(() => {
        const tabs: { label: string, value: string }[] = [];

        const { classification, regression } = modelFamilyModels;
        const allClassificationAreDcm = classification.length > 0 && dcmModelFamilyIds.length === classification.length;

        if (classification.length > 0 && regression.length > 0) {
            tabs.push({ label: 'Classification Prediction Scores', value: 'classification' });
            tabs.push({ label: 'Regression Prediction Scores', value: 'regression' }); 
        } else {
            tabs.push({
                label: classification.length > 0
                    ? allClassificationAreDcm
                        ? 'DCM Prediction Scores'
                        : 'Prediction Scores'
                    : 'Prediction Scores',
                value: classification.length > 0 ? 'classification' : 'regression'
            });
        }

        const hasNonConformalDcm = groupedResultsetData.some((group: ResultsetGrouping) => group.hasDcm && !group.isConformal);
        if (hasNonConformalDcm) {
            tabs.push({ label: 'DCM Trust', value: 'dcm-trust' });
        }

        tabs.push({ label: 'Explainability', value: 'explainability' });

        tabs.push({ label: 'Visualizations', value: 'visualizations' });

        return tabs;
    }, [modelFamilyModels, dcmModelFamilyIds, groupedResultsetData]);

    const modelFamilyOptions: { name: string, id: number }[] = useMemo(() => {
        const options: { name: string, id: number }[] = [];

        const { classification, regression } = modelFamilyModels;

        [classification, regression].forEach((modelGroup: { modelFamily: ModelFamily, models: Model[] }[]) => {
            if (modelGroup.length > 0) {
                modelGroup.forEach((group: { modelFamily: ModelFamily, models: Model[] }) => {
                    options.push({
                        name: group.modelFamily.name ?? '',
                        id: group.modelFamily.id ?? 0,
                    })
                })
            }
        });

        return options;
    }, [modelFamilyModels]);

    const modelHashLabels = useMemo(() => {
        const labels: { [key: string]: string } = {};

        // Prefer formatter-derived header labels, keyed by model hash.
        groupedResultsetData.forEach((group: ResultsetGrouping) => {
            (group.modelHeaders ?? []).forEach((header: TableHeader) => {
                if (!header?.value || !header?.label) {
                    return;
                }
                labels[String(header.value)] = String(header.label);
            });
        });

        // Fallback to prediction names when model headers are not yet available.
        predictions.forEach((prediction: Prediction) => {
            const modelHash = prediction.model_hash;
            if (!modelHash || labels[String(modelHash)]) {
                return;
            }
            const displayName = prediction.name ? String(prediction.name) : `Model ${String(modelHash).slice(0, 12)}`;
            labels[String(modelHash)] = displayName;
        });

        return labels;
    }, [groupedResultsetData, predictions]);

    useEffect(() => {
        if (modelFamilyOptions.length === 0) {
            setChosenExplainabilityModelFamily(null);
            return;
        }

        if (!chosenExplainabilityModelFamily) {
            setChosenExplainabilityModelFamily(modelFamilyOptions[0]);
            return;
        }

        const exists = modelFamilyOptions.some((option) => option.id === chosenExplainabilityModelFamily.id);
        if (!exists) {
            setChosenExplainabilityModelFamily(modelFamilyOptions[0]);
        }
    }, [modelFamilyOptions, chosenExplainabilityModelFamily]);

    useEffect(() => {
        if (dataframe && prediction && pageTabs.length > 0) {
            const selectedTab = pageTabs[selectedIndex].value;

            if (selectedTab !== 'classification' && selectedTab !== 'regression') {
                return;
            }

            let selectedModelFamilyIds: number[] = [];

            if (selectedTab === 'classification' && modelFamilyModels.classification.length > 0) {
                selectedModelFamilyIds = modelFamilyModels.classification
                    .map((group: { modelFamily: ModelFamily, models: Model[] }) => group.modelFamily.id)
                    .filter((value: number | undefined | null): value is number => value !== undefined && value !== null);
            } else if (selectedTab === 'regression' && modelFamilyModels.regression.length > 0) {
                selectedModelFamilyIds = modelFamilyModels.regression
                    .map((group: { modelFamily: ModelFamily, models: Model[] }) => group.modelFamily.id)
                    .filter((value: number | undefined | null): value is number => value !== undefined && value !== null);
            }

            const selectedGroups = groupedResultsetData.filter((group: ResultsetGrouping) => selectedModelFamilyIds.includes(group.modelFamilyId));
            const hasAvailableProba = selectedGroups.some((group: ResultsetGrouping) => Boolean(group.hasProba));
            if (!hasAvailableProba) {
                setUseProba(false);
            }
            setHasProba(hasAvailableProba);

            const hasConformalPredictions = selectedGroups.some((group: ResultsetGrouping) => Boolean(group.isConformal));
            setIsConformal(hasConformalPredictions);

            const { headers: dataHeaders, data: formattedItems } = buildSingleCompoundComparisonData(
                groupedResultsetData,
                selectedModelFamilyIds,
                prediction?.resultset_metadata?.smiles,
            );

            setItems(formattedItems);
            setPredictionData(formattedItems);
            setHeaders(dataHeaders);

            
        }
        //eslint-disable-next-line
    }, [dataframe, groupedResultsetData, modelFamilyModels, selectedIndex, prediction, pageTabs]);

    const activeDcmGrouping = useMemo(() => {
        if (groupedResultsetData.length === 0) return null;

        const selectedTab = pageTabs[selectedIndex]?.value;
        const classificationFamilyIds = modelFamilyModels.classification
            .map((group: { modelFamily: ModelFamily, models: Model[] }) => group.modelFamily.id)
            .filter((value: number | undefined | null): value is number => value !== undefined && value !== null);
        const regressionFamilyIds = modelFamilyModels.regression
            .map((group: { modelFamily: ModelFamily, models: Model[] }) => group.modelFamily.id)
            .filter((value: number | undefined | null): value is number => value !== undefined && value !== null);

        let candidateGroups = groupedResultsetData;
        if (selectedTab === 'classification') {
            candidateGroups = groupedResultsetData.filter((group: ResultsetGrouping) => classificationFamilyIds.includes(group.modelFamilyId));
        } else if (selectedTab === 'regression') {
            candidateGroups = groupedResultsetData.filter((group: ResultsetGrouping) => regressionFamilyIds.includes(group.modelFamilyId));
        }

        const matchingGroup = candidateGroups.find((group: ResultsetGrouping) => group.hasDcm && !group.isConformal);
        if (matchingGroup) {
            return matchingGroup;
        }

        return groupedResultsetData.find((group: ResultsetGrouping) => group.hasDcm && !group.isConformal) ?? null;
    }, [groupedResultsetData, modelFamilyModels, pageTabs, selectedIndex]);

    useEffect(() => {
        if (!activeDcmGrouping || !activeDcmGrouping.hasDcm) {
            setSelectedDcmModelHash(null);
            return;
        }

        const dcmHashes = activeDcmGrouping.dcmModelHashes ?? [];
        if (dcmHashes.length === 0) {
            setSelectedDcmModelHash(null);
            return;
        }

        if (!selectedDcmModelHash || !dcmHashes.includes(selectedDcmModelHash)) {
            setSelectedDcmModelHash(dcmHashes[0]);
        }
    }, [activeDcmGrouping, selectedDcmModelHash]);

    useEffect(() => {
        if (!activeDcmGrouping || !activeDcmGrouping.hasDcm) {
            return;
        }

        const defaultThreshold = getDefaultDcmThreshold(activeDcmGrouping, predictions, selectedDcmModelHash);
        setDcmThreshold(defaultThreshold);
    }, [activeDcmGrouping, predictions, selectedDcmModelHash]);

    const dropdownOptions: DropdownOption[] = [
        {
            label: 'Export',
            action: (e:MouseEvent<HTMLButtonElement>) => {},
            moreOptions: exportFileExtensions.map(ext => (
                {
                    label: ext.label,
                    action: async (e: MouseEvent<HTMLButtonElement>) => {
                        e.preventDefault();
                        const name = prediction && prediction.name ? prediction.name : '';
                        try {
                            await exportData(
                                [{ name, hash: id ? id : '' }],
                                ext.file_type,
                                'resultset',
                            );

                            setPopupMessage('You have successfully downloaded the selected resultset.', true);
                        } catch (err:any) {
                            console.log(err);
                            setPopupMessage('There was an error downloading this resultset. Please try again.', false);
                        }
                    }
                }
            ))
        },
        {
            label: 'Delete',
            btnComp: (
                <DeleteModal 
                    items={[{
                        type: 'resultset',
                        id: id ? id : '',
                        name: prediction && prediction.name ? prediction.name : '',
                    }]}
                    deleteFn={deleteResultsets}
                />
            ),
        }
    ];

    const handleTabChange = (event: SyntheticEvent, index: number) => {
        setSelectedIndex(index);
    }

    const handleChooseExplainabilityModelFamily = (e:any) => {
        const matchingModelFamily = modelFamilyOptions.find(option => option.id === e?.value);
        if (matchingModelFamily) {
            setChosenExplainabilityModelFamily(matchingModelFamily);
        }
    }

    


    return (
        <div className="p-10 h-full min-h-0 overflow-hidden flex flex-col justify-start items-stretch gap-4"> 
            <SinglePageHeader 
                id='single-prediction-header' 
                label={prediction ? prediction.name! : ''} 
                isLoading={isLoading} 
                pages={[
                    {
                        label: 'Resultsets',
                        action: (e: MouseEvent<HTMLButtonElement>) => {navigate('/resultsets')}
                    },
                    {
                        label: prediction ? prediction.name! : '',
                    }
                ]}
                // itemMetadata={[{
                //     label: 'Records',
                //     value: resultsetDataframe ? getResultsetRecords(resultsetDataframe).toString() : '0',
                //     adornment: <RecordsIcon/>,
                // }]}
            >
                <NestedDropdown
                    dir='right'
                    btn={{
                        label: 'Options',
                        type: 'primary',
                        styling: 'solid',
                        id: 'single-file-options'
                    }}
                    options={dropdownOptions}
                />
                {predictionMetadata.length > 0 && (
                    <UpdatedMetadataMenu 
                        menuLabel="Prediction Metadata"
                        menuItems={predictionMetadata}
                        location="page"
                    />
                )}
            </SinglePageHeader>

            {pageTabs.length > 0 && (
                <div className="mb-4">
                    <NewPageTabs 
                        tabs={pageTabs.map((tab, i) => ({
                            label: tab.label,
                            index: i,
                        }))}
                        selectedIndex={selectedIndex}
                        handleTabChange={handleTabChange}
                    />
                </div>
            )}

            {pageTabs.length === 0 || pageTabs[selectedIndex].value === 'classification' || pageTabs[selectedIndex].value === 'regression' ? (

            <>
            {isConformal && (
                <ResultComparison 
                    data={predictionData}
                    isLoading={isLoading} 
                    threshold={threshold}
                    setThreshold={setThreshold}
                    headers={headers}
                />
            )}

            <div className="grow flex justify-between items-start gap-8">
                <div className="flex flex-col justify-start items-stretch gap-4 grow h-full">
                    <div className="w-full flex justify-between items-center gap-4">
                        <div className="flex justify-end items-center gap-4">
                            {isConformal && (
                                <HorizontalNumberSlider 
                                    label="Alpha Threshold"
                                    threshold={threshold}
                                    setThreshold={setThreshold}
                                    hasBorderAndPadding
                                />
                            )}
                            
                        </div>
                        <div className="flex justify-end items-center gap-4">
                            {!isConformal && hasProba && (
                                <div className="min-w-[100px] pb-7">
                                    <BooleanField
                                        label="Use Probability"
                                        id="use-proba-toggle"
                                        name="useProba"
                                        value={useProba}
                                        isRequired={false}
                                        handleChange={(_, __) => setUseProba((prev: boolean) => !prev)}
                                        disabled={false}
                                    />
                                </div>
                            )}
                            {headers && <ColumnsButton 
                                headers={headers} 
                                setHeaders={setHeaders} 
                                isDisabled={false}
                            />}
                        </div>
                    </div>

                    <VirtualizedDataTable 
                        isLoading={isLoading} 
                        headers={headers} 
                        tableData={paginatedItems}
                        statsType="prediction"
                        threshold={isConformal ? threshold : undefined}
                        setPopupMessage={setPopupMessage}
                        fixedItemHeight={63}
                    />   

                    <Pagination
                        setItemsPerPage={setItemsPerPage}
                        setCurrentPage={setCurrentPage}
                        currentPage={currentPage}
                        startIndex={startIndex}
                        endIndex={endIndex}
                        totalPages={totalPages}
                        total={predictionData.length}
                        paginate={paginate}
                    />
                </div>

                <div className="flex flex-col justify-start items-stretch gap-4 w-[350px]">
                    <div className="w-full flex flex-col justify-start items-start gap-4">
                        {isLoading ? (
                            <>
                                <Skeleton variant='text' style={{ fontSize: '18px' }} width='100%'/>
                                <Skeleton variant='rounded' width='100%' height={290} />
                            </>
                        ) : prediction && prediction.resultset_metadata!.smiles ? (
                            <>
                                <p className="text-[18px] text-default font-semibold">Predicted Compound</p>
                                <div className={`w-full rounded-xl bg-white border border-outline flex flex-col justify-start items-stretch`}>
                                    <div className="w-full p-3 flex justify-center items-center">
                                        <MoleculeStructure smiles={prediction.resultset_metadata!.smiles!} height={200} width={200}/>
                                    </div>
                                    <div className="w-full flex flex-col justify-start items-stretch gap-6 p-6 border-t border-outline">
                                        <p className={`font-semibold text-default text-[16px] max-w-full truncate`}>
                                            {prediction.resultset_metadata!.smiles!}
                                        </p>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <></>
                        )}
                    </div>
                </div>
            </div>
            </>

            ) : pageTabs[selectedIndex].value === 'explainability' ? (
                <div className="grow min-h-0 flex flex-col justify-start items-stretch gap-4">
                    {modelFamilyOptions.length > 0 && (
                        <div className="max-w-[600px]">
                            <SelectField
                                label="Model Family"
                                name="explainability_model_family"
                                id="explainability_model_family"
                                value={chosenExplainabilityModelFamily ? { value: chosenExplainabilityModelFamily.id, label: chosenExplainabilityModelFamily.name } : null}
                                options={modelFamilyOptions.map(option => ({ value: option.id, label: option.name }))}
                                isCreatable={false}
                                isRequired={false}
                                isMulti={false}
                                handleChange={handleChooseExplainabilityModelFamily}
                            />
                        </div>
                    )}

                    {prediction?.resultset_metadata?.smiles && (
                        <div className="w-full rounded-xl bg-white border border-outline flex justify-start items-center gap-4 p-4">
                            <MoleculeStructure smiles={prediction.resultset_metadata.smiles} height={72} width={72}/>
                            <div className="flex flex-col justify-center items-start">
                                <p className="text-[14px] text-default/70 font-medium">Predicted Compound</p>
                                <p className="text-[14px] text-default font-semibold break-all">{prediction.resultset_metadata.smiles}</p>
                            </div>
                        </div>
                    )}

                    <div className="grow min-h-0 overflow-y-auto">
                        <ResultsetExplainabilityTab
                            resultsetId={id ? Number(id) : 0}
                            resultsetDataframeV2={dataframe}
                            chosenModelFamilyId={chosenExplainabilityModelFamily ? chosenExplainabilityModelFamily.id : null}
                            modelHashLabels={modelHashLabels}
                            compactSingleMoleculeMode
                        />
                    </div>
                </div>
            ) : pageTabs[selectedIndex].value === 'dcm-trust' ? (
                <DCMTrustTab
                    grouping={activeDcmGrouping}
                    threshold={dcmThreshold}
                    setThreshold={setDcmThreshold}
                    selectedModelHash={selectedDcmModelHash}
                    setSelectedModelHash={setSelectedDcmModelHash}
                    modelHashLabels={modelHashLabels}
                />
            ) : (
                <ResultsetVisualizations 
                    setPopupMessage={setPopupMessage}
                    resultset={prediction}
                    modelFamilyOptions={modelFamilyOptions}
                />
            )}

        <SnackBar open={open} setOpen={setOpen} message={message} isSuccess={isSuccess}/>  
            
    </div>
    )
}